import styled from "@emotion/styled";
import BaseButton from "src/atoms/Button/BaseButton";
import { PRODUCT_CAROUSEL_SLIDE_WIDTH } from "src/utils/constants";
export type CarouselMarginSize = "xl" | "l" | "m" | "s";

const fallbackSlideWidth = {
  mobile: `calc(${PRODUCT_CAROUSEL_SLIDE_WIDTH.mobile} - 24px)`,
  tablet: `calc(${PRODUCT_CAROUSEL_SLIDE_WIDTH.tablet} - 24px)`,
  desktop: `calc(${PRODUCT_CAROUSEL_SLIDE_WIDTH.desktop} - 24px)`,
};

const spaceBetweenMobile = (marginSizeMobile: CarouselMarginSize) => {
  switch (marginSizeMobile) {
    case "s":
      return 4;
    case "m":
      return 8;
    case "l":
      return 16;
    default:
      return 24; // NOTE: "xl"
  }
};

const StyledSwiperWrapper = styled.div<{
  marginSizeMobile?: CarouselMarginSize;
  slideShadowSpace?: number;
}>`
  &.slide-shadow-space {
    overflow: hidden;
    .swiper-slide-visible {
      visibility: visible;
    }

    .swiper-slide-invisible {
      visibility: hidden;
    }

    &.with-horizontal-padding {
      margin-inline: -${({ slideShadowSpace }) => slideShadowSpace || 0}px;

      .swiper-container {
        padding-inline: ${({ slideShadowSpace }) => slideShadowSpace || 0}px;
      }
    }

    &.with-vertical-padding {
      margin-block: -${({ slideShadowSpace }) => slideShadowSpace || 0}px;

      .swiper-container {
        padding-block: ${({ slideShadowSpace }) => slideShadowSpace || 0}px;
      }
    }
  }

  .swiper-container {
    overflow: unset;

    @media (max-width: ${({ theme }) => theme.breakpoints[1]}) {
      z-index: 0;
    }
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoints[2]}) {
    &.fit-inside-container {
      padding: 0
        ${({ marginSizeMobile, theme }) =>
          (marginSizeMobile && `${spaceBetweenMobile(marginSizeMobile)}px`) ||
          theme.space.space16 ||
          "16px"};
    }

    &.fit-regular {
      &:not(.is-retro-revival) .swiper-wrapper {
        scroll-padding-inline-start: ${({ theme, marginSizeMobile }) =>
          (marginSizeMobile && `${spaceBetweenMobile(marginSizeMobile)}px`) ||
          theme.space.space16 ||
          "16px"};
      }

      &:not(.is-retro-revival) .swiper-slide:first-of-type {
        margin-left: ${({ theme, marginSizeMobile }) =>
          (marginSizeMobile && `${spaceBetweenMobile(marginSizeMobile)}px`) ||
          theme.space.space16 ||
          "16px"};
      }

      .swiper-slide {
        &:last-of-type {
          margin-right: ${({ theme, marginSizeMobile }) =>
            (marginSizeMobile && `${spaceBetweenMobile(marginSizeMobile)}px`) ||
            theme.space.space16 ||
            "16px"};
        }
      }
    }
  }

  .swiper-button-disabled {
    display: none;
  }

  .swiper-slide {
    width: auto;
    margin-right: unset;

    @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
      width: auto;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
      width: auto;
    }
  }

  &.is-server-render {
    .swiper-wrapper {
      overflow: auto;
    }

    .swiper-slide {
      width: 30%;
      margin-right: 24px;

      @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
        width: 22%;
      }

      @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
        width: 33%;
      }
    }

    &.is-retro-revival {
      .swiper-slide {
        width: 100%;
      }
    }

    &.is-product-carousel {
      .swiper-slide {
        width: ${fallbackSlideWidth.mobile};

        @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
          width: ${fallbackSlideWidth.tablet};
        }

        @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
          width: ${fallbackSlideWidth.desktop};
        }
      }
    }
  }
`;

const styledArrow = ({
  theme,
  showInfoText,
}: {
  theme: any;
  showInfoText?: boolean;
}) => `
  position: absolute;
  z-index: 20;
  width: 32px;
  height: 32px;
  background-color: ${theme.colors.tone.white};
  border: 2px solid ${theme.colors.tone.silver};
  display: none;
  cursor: pointer;
  @media screen and (min-width: ${theme.breakpoints[3] as string}) {
    &:not([disabled]) {
      display: block;
    }
  }
  transform: translate(-50%, -50%);
  
  ${
    showInfoText
      ? `&:not([disabled]) {
      @media screen and (min-width: ${theme.breakpoints[1] as string}) {
        display: block;
      }
    }`
      : ""
  }
`;

const StyledButtonBack = styled(BaseButton)<{
  offsetPosition?: string | null;
}>`
  left: 0;
  top: ${({ offsetPosition }) => offsetPosition};
  border-radius: 24px;
  ${styledArrow}
`;

const StyledButtonNext = styled(BaseButton)<{
  offsetPosition?: string | null;
}>`
  left: 100%;
  top: ${({ offsetPosition }) => offsetPosition};
  border-radius: 24px;
  ${styledArrow}
`;

const CarouselTemplate = styled.div`
  position: relative;

  .sr-only {
    display: none;
  }
`;

export {
  CarouselTemplate,
  StyledSwiperWrapper,
  StyledButtonNext,
  StyledButtonBack,
};
